import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { LucideAngularModule, X, GripVertical } from 'lucide-angular';

const SIDEBAR_WIDTH_KEY = 'sidebar-width';
const DEFAULT_WIDTH = 35;
const MAX_WIDTH = 50;
const MIN_WIDTH = 20;

@Component({
  selector: 'app-right-sidebar',
  templateUrl: './right-sidebar.component.html',
  styleUrls: [],
  imports: [NgClass, NgStyle, LucideAngularModule],
  standalone: true,
})
export class RightSidebarComponent implements OnInit, OnDestroy {
  @Input() isSidebarOpen: boolean = false;
  @Input() customWidth?: number;
  @Output() closed = new EventEmitter<unknown>();

  protected sidebarWidth: number = DEFAULT_WIDTH;
  protected isResizing: boolean = false;
  protected startX: number = 0;
  protected startWidth: number = 0;

  ngOnInit(): void {
    if (this.customWidth) {
      this.sidebarWidth = this.getResponsiveWidth(this.customWidth);
    } else {
      this.loadSidebarWidth();
    }
    
    // Listen for window resize to update responsive width
    window.addEventListener('resize', this.handleWindowResize);
  }

  ngOnDestroy(): void {
    // Clean up any event listeners that might still be active
    if (this.isResizing) {
      document.removeEventListener('mousemove', this.resize);
      document.removeEventListener('mouseup', this.stopResize);
      document.removeEventListener('touchmove', this.resize);
      document.removeEventListener('touchend', this.stopResize);
    }
    window.removeEventListener('resize', this.handleWindowResize);
  }

  closeSidebar(): void {
    this.isSidebarOpen = false;
    this.closed.emit();
  }

  startResize(event: MouseEvent | TouchEvent): void {
    this.isResizing = true;

    // Get the X coordinate from either mouse or touch event
    if (event instanceof MouseEvent) {
      this.startX = event.clientX;
    } else if (event instanceof TouchEvent && event.touches.length > 0) {
      this.startX = event.touches[0].clientX;
    }

    this.startWidth = this.sidebarWidth;

    // Add event listeners to handle resizing for both mouse and touch
    document.addEventListener('mousemove', this.resize);
    document.addEventListener('mouseup', this.stopResize);
    document.addEventListener('touchmove', this.resize, { passive: false });
    document.addEventListener('touchend', this.stopResize);

    // Prevent default behavior for touch events
    event.preventDefault();
  }

  resize = (event: MouseEvent | TouchEvent): void => {
    if (!this.isResizing) return;

    const windowWidth = window.innerWidth;
    let clientX: number;

    // Get the X coordinate from either mouse or touch event
    if (event instanceof MouseEvent) {
      clientX = event.clientX;
    } else if (event instanceof TouchEvent && event.touches.length > 0) {
      clientX = event.touches[0].clientX;
      // Prevent default behavior for touch events to avoid scrolling
      event.preventDefault();
    } else {
      return;
    }

    const dx = this.startX - clientX;

    // Calculate the new width as a percentage of the window width
    const newWidthPercent = this.startWidth + (dx / windowWidth) * 100;

    // Limit the width between MIN_WIDTH and MAX_WIDTH
    this.sidebarWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidthPercent));
  };

  stopResize = (): void => {
    this.isResizing = false;
    document.removeEventListener('mousemove', this.resize);
    document.removeEventListener('mouseup', this.stopResize);
    document.removeEventListener('touchmove', this.resize);
    document.removeEventListener('touchend', this.stopResize);

    // Save the width to localStorage
    this.saveSidebarWidth();
  };

  private loadSidebarWidth(): void {
    const savedWidth = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    if (savedWidth) {
      this.sidebarWidth = parseFloat(savedWidth);
    }
  }

  private saveSidebarWidth(): void {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, this.sidebarWidth.toString());
  }

  private getResponsiveWidth(baseWidth: number): number {
    const windowWidth = window.innerWidth;
    
    // For very narrow screens (mobile), use a larger percentage
    if (windowWidth < 640) {
      return Math.min(80, Math.max(60, baseWidth + 20));
    }
    // For tablet screens, use a moderate increase
    else if (windowWidth < 1024) {
      return Math.min(60, Math.max(40, baseWidth + 10));
    }
    // For desktop, use the base width
    else {
      return baseWidth;
    }
  }

  private handleWindowResize = (): void => {
    if (this.customWidth && !this.isResizing) {
      this.sidebarWidth = this.getResponsiveWidth(this.customWidth);
    }
  };

  protected readonly XIcon = X;
  protected readonly GripVertical = GripVertical;
}
